const PROTOCOL_VERSION = 'rtm-ecdh-v1';
const HANDSHAKE_CLIENT_OK = 'rtm-handshake-ok';
const HANDSHAKE_SERVER_OK = 'rtm-handshake-server-ok';

export const HANDSHAKE_EVENTS = {
  INIT: 'key_exchange_init',
  RESPONSE: 'key_exchange_response',
  COMPLETE: 'key_exchange_complete',
  ACK: 'key_exchange_ack',
  ERROR: 'key_exchange_error',
} as const;

export interface EncryptedEnvelope {
  encrypted: true;
  seq: number;
  iv: string;
  ciphertext: string;
  tag: string;
}

export interface DirectionalKeys {
  k_c2s: CryptoKey;
  k_s2c: CryptoKey;
}

function encodeBase64(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

function decodeBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function concatBuffers(...parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  parts.forEach((part) => {
    out.set(part, offset);
    offset += part.length;
  });
  return out;
}

function sequenceToAad(seq: number): Uint8Array {
  const aad = new Uint8Array(4);
  new DataView(aad.buffer).setUint32(0, seq);
  return aad;
}

export function isEncryptedEnvelope(data: unknown): data is EncryptedEnvelope {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const envelope = data as EncryptedEnvelope;
  return (
    envelope.encrypted === true &&
    typeof envelope.seq === 'number' &&
    typeof envelope.iv === 'string' &&
    typeof envelope.ciphertext === 'string' &&
    typeof envelope.tag === 'string'
  );
}

export async function generateEphemeralKeyPair(): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits']);
}

export async function exportUncompressedPublicKey(publicKey: CryptoKey): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.exportKey('raw', publicKey));
}

export async function importUncompressedPublicKey(bytes: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', bytes, { name: 'ECDH', namedCurve: 'P-256' }, true, []);
}

export async function computeSharedSecret(privateKey: CryptoKey, peerPublicKey: CryptoKey): Promise<Uint8Array> {
  const bits = await crypto.subtle.deriveBits({ name: 'ECDH', public: peerPublicKey }, privateKey, 256);
  return new Uint8Array(bits);
}

async function deriveAesKey(
  sharedSecret: Uint8Array,
  c2sNonce: Uint8Array,
  s2cNonce: Uint8Array,
  info: string
): Promise<CryptoKey> {
  const salt = concatBuffers(c2sNonce, s2cNonce);
  const hkdfKey = await crypto.subtle.importKey('raw', sharedSecret, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt,
      info: new TextEncoder().encode(info),
    },
    hkdfKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function deriveDirectionalKeys(
  sharedSecret: Uint8Array,
  c2sNonce: Uint8Array,
  s2cNonce: Uint8Array
): Promise<DirectionalKeys> {
  const [k_c2s, k_s2c] = await Promise.all([
    deriveAesKey(sharedSecret, c2sNonce, s2cNonce, 'c2s'),
    deriveAesKey(sharedSecret, c2sNonce, s2cNonce, 's2c'),
  ]);
  return { k_c2s, k_s2c };
}

export function buildSignaturePayload(
  clientPublicKey: Uint8Array,
  serverPublicKey: Uint8Array,
  c2sNonce: Uint8Array,
  s2cNonce: Uint8Array,
  sessionId: string
): Uint8Array {
  return concatBuffers(
    clientPublicKey,
    serverPublicKey,
    c2sNonce,
    s2cNonce,
    new TextEncoder().encode(sessionId)
  );
}

export async function importSigningPublicKey(pem: string): Promise<CryptoKey> {
  const normalized = pem.replace(/\\n/g, '\n').trim();
  const base64 = normalized
    .replace('-----BEGIN PUBLIC KEY-----', '')
    .replace('-----END PUBLIC KEY-----', '')
    .replace(/\s/g, '');
  const der = decodeBase64(base64);
  return crypto.subtle.importKey('spki', der, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);
}

export async function verifyHandshakeSignature(
  signatureB64: string,
  clientPublicKey: Uint8Array,
  serverPublicKey: Uint8Array,
  c2sNonce: Uint8Array,
  s2cNonce: Uint8Array,
  sessionId: string,
  signingPublicKey: CryptoKey
): Promise<boolean> {
  const payload = buildSignaturePayload(clientPublicKey, serverPublicKey, c2sNonce, s2cNonce, sessionId);
  const signature = decodeBase64(signatureB64);
  return crypto.subtle.verify({ name: 'ECDSA', hash: 'SHA-256' }, signingPublicKey, signature, payload);
}

export async function encrypt(key: CryptoKey, plaintext: string, seq: number): Promise<EncryptedEnvelope> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
      additionalData: sequenceToAad(seq),
      tagLength: 128,
    },
    key,
    new TextEncoder().encode(plaintext)
  );
  const cipherBytes = new Uint8Array(ciphertext);
  const tag = cipherBytes.slice(cipherBytes.length - 16);
  const body = cipherBytes.slice(0, cipherBytes.length - 16);
  return {
    encrypted: true,
    seq,
    iv: encodeBase64(iv),
    ciphertext: encodeBase64(body),
    tag: encodeBase64(tag),
  };
}

export async function decrypt(key: CryptoKey, envelope: EncryptedEnvelope, seq: number): Promise<string> {
  const iv = decodeBase64(envelope.iv);
  const ciphertext = decodeBase64(envelope.ciphertext);
  const tag = decodeBase64(envelope.tag);
  const combined = new Uint8Array(ciphertext.length + tag.length);
  combined.set(ciphertext, 0);
  combined.set(tag, ciphertext.length);
  const plainBuffer = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv,
      additionalData: sequenceToAad(seq),
      tagLength: 128,
    },
    key,
    combined
  );
  return new TextDecoder().decode(plainBuffer);
}

export async function encryptPayload(key: CryptoKey, payload: unknown, seq: number): Promise<EncryptedEnvelope> {
  const plaintext = typeof payload === 'string' ? payload : JSON.stringify(payload ?? {});
  return encrypt(key, plaintext, seq);
}

export async function decryptPayload(key: CryptoKey, envelope: EncryptedEnvelope, seq: number): Promise<any> {
  const plaintext = await decrypt(key, envelope, seq);
  try {
    return JSON.parse(plaintext);
  } catch {
    return plaintext;
  }
}

export function randomNonce4(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(4));
}

export {
  PROTOCOL_VERSION,
  HANDSHAKE_CLIENT_OK,
  HANDSHAKE_SERVER_OK,
  encodeBase64,
  decodeBase64,
};
